import { UISimulation } from '@/components/help/UISimulation';

const D = 1800;

function Shell({
  step,
  total,
  title,
  children,
}: {
  step: number;
  total: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted">
          Step {step} of {total}
        </div>
        <div className="text-[11px] font-mono text-dash-secondary">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Para({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="text-xs text-dash-secondary leading-relaxed">{children}</p>
  );
}

function ShorthandCard({
  name,
  body,
}: {
  name: string;
  body: string;
}): JSX.Element {
  return (
    <div className="mt-2 rounded border border-accent/40 bg-accent/5 px-2.5 py-2">
      <div className="text-[10px] font-mono uppercase tracking-wide text-accent">
        [{name}]
      </div>
      <div className="text-xs text-dash-primary mt-1">{body}</div>
    </div>
  );
}

// Editor overview — 5 frames showing prose, cards, decorations, flags,
// annotations layered on the same article.
function OverviewProseFrame(): JSX.Element {
  return (
    <Shell step={1} total={5} title="Prose">
      <Para>
        Espresso machines need a descale every six to eight weeks on
        municipal water to keep flow rates consistent.
      </Para>
    </Shell>
  );
}

function OverviewCardsFrame(): JSX.Element {
  return (
    <Shell step={2} total={5} title="Component cards">
      <Para>
        Espresso machines need a descale every six to eight weeks on
        municipal water to keep flow rates consistent.
      </Para>
      <ShorthandCard
        name="QuickAnswer"
        body="Descale every 6-8 weeks on city water; every 12 weeks on filtered."
      />
    </Shell>
  );
}

function OverviewInterlinksFrame(): JSX.Element {
  return (
    <Shell step={3} total={5} title="Interlinks">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Espresso machines need a{' '}
        <span className="underline decoration-accent decoration-dotted">
          descale
        </span>{' '}
        every six to eight weeks on{' '}
        <span className="underline decoration-accent decoration-dotted">
          municipal water
        </span>{' '}
        to keep flow rates consistent.
      </p>
    </Shell>
  );
}

function OverviewFlagsFrame(): JSX.Element {
  return (
    <Shell step={4} total={5} title="Factual flags">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Espresso machines need a descale{' '}
        <span className="bg-warning/15 text-warning rounded px-1">
          every six to eight weeks
        </span>{' '}
        on municipal water to keep flow rates consistent.
      </p>
      <div className="mt-2 rounded border border-warning/40 bg-warning/10 px-2 py-1.5 text-[11px] text-warning">
        Reviewer wants a source for this interval.
      </div>
    </Shell>
  );
}

function OverviewAnnotationsFrame(): JSX.Element {
  return (
    <Shell step={5} total={5} title="Annotations">
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <p className="text-xs text-dash-secondary leading-relaxed">
          Espresso machines need a descale every{' '}
          <span className="underline decoration-violet-400 decoration-2">
            six to eight weeks
          </span>{' '}
          on municipal water.
        </p>
        <div className="rounded border border-violet-400/40 bg-violet-400/10 px-2 py-1.5 text-[11px] text-dash-primary">
          <div className="font-semibold text-violet-300">Cite a study</div>
          <div className="text-dash-secondary">Pull a number we trust.</div>
        </div>
      </div>
    </Shell>
  );
}

export function EditorOverviewSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <OverviewProseFrame />, label: 'Plain prose', duration: D },
        { frame: <OverviewCardsFrame />, label: 'Component cards inline', duration: D },
        { frame: <OverviewInterlinksFrame />, label: 'Interlink decorations', duration: D },
        { frame: <OverviewFlagsFrame />, label: 'Factual flag markers', duration: D },
        { frame: <OverviewAnnotationsFrame />, label: 'Annotation highlights', duration: D },
      ]}
    />
  );
}

// Shorthand insert — 4 frames.
function ShortType(): JSX.Element {
  return (
    <Shell step={1} total={4} title="Type [">
      <Para>You type [ at the end of a paragraph.</Para>
      <div className="mt-2 rounded border border-dash-border bg-dash-surface px-3 py-2 text-xs text-dash-primary font-mono">
        Drink one glass of water before you start.[
      </div>
    </Shell>
  );
}

function ShortPopup(): JSX.Element {
  return (
    <Shell step={2} total={4} title="Autocomplete">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2 text-xs text-dash-primary font-mono">
        Drink one glass of water before you start.[
      </div>
      <ul className="mt-2 rounded border border-accent/40 bg-dash-surface text-xs divide-y divide-dash-border">
        <li className="px-3 py-1.5 bg-accent/10 text-dash-primary">FAQ</li>
        <li className="px-3 py-1.5 text-dash-secondary">QuickAnswer</li>
        <li className="px-3 py-1.5 text-dash-secondary">Steps</li>
      </ul>
    </Shell>
  );
}

function ShortInsert(): JSX.Element {
  return (
    <Shell step={3} total={4} title="Insert FAQ">
      <Para>Drink one glass of water before you start.</Para>
      <ShorthandCard name="FAQ" body="0 questions — click Edit to add some." />
    </Shell>
  );
}

function ShortRendered(): JSX.Element {
  return (
    <Shell step={4} total={4} title="Filled card">
      <Para>Drink one glass of water before you start.</Para>
      <div className="mt-2 rounded border border-accent/40 bg-accent/5 px-3 py-2">
        <div className="text-[10px] font-mono uppercase tracking-wide text-accent">
          [FAQ]
        </div>
        <div className="mt-1 text-xs">
          <div className="text-dash-primary font-medium">
            Is hot or cold water better?
          </div>
          <div className="text-dash-secondary">
            Cold; warm water carries less oxygen and tastes flat.
          </div>
        </div>
      </div>
    </Shell>
  );
}

export function ShorthandInsertSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <ShortType />, label: 'Type [ in prose', duration: D },
        { frame: <ShortPopup />, label: 'Autocomplete opens', duration: D },
        { frame: <ShortInsert />, label: 'Pick FAQ', duration: D },
        { frame: <ShortRendered />, label: 'Edit the card', duration: D },
      ]}
    />
  );
}

// Interlink popover — 3 frames.
function ILText(): JSX.Element {
  return (
    <Shell step={1} total={3} title="Underlined link">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Espresso machines need a{' '}
        <span className="underline decoration-accent decoration-dotted">
          descale
        </span>{' '}
        every six weeks.
      </p>
    </Shell>
  );
}

function ILPopover(): JSX.Element {
  return (
    <Shell step={2} total={3} title="Popover opens">
      <div className="rounded border border-accent/40 bg-dash-surface px-3 py-2 text-xs">
        <div className="text-[10px] font-mono uppercase text-dash-muted">
          Anchor
        </div>
        <div className="text-dash-primary">descale</div>
        <div className="mt-2 text-[10px] font-mono uppercase text-dash-muted">
          Target
        </div>
        <div className="text-dash-primary">/articles/descaling-frequency</div>
        <div className="mt-2 flex gap-2">
          <span className="rounded bg-accent/15 text-accent border border-accent/40 px-2 py-0.5">
            Save
          </span>
          <span className="rounded border border-dash-border text-dash-secondary px-2 py-0.5">
            Next match
          </span>
        </div>
      </div>
    </Shell>
  );
}

function ILEdit(): JSX.Element {
  return (
    <Shell step={3} total={3} title="Saved">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Espresso machines need a{' '}
        <span className="underline decoration-accent decoration-solid">
          descale
        </span>{' '}
        every six weeks.
      </p>
      <div className="mt-2 text-[10px] font-mono uppercase tracking-wide text-success">
        link saved
      </div>
    </Shell>
  );
}

export function InterlinkPopoverSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <ILText />, label: 'Click the underline', duration: D },
        { frame: <ILPopover />, label: 'Edit anchor + target', duration: D },
        { frame: <ILEdit />, label: 'First occurrence linked', duration: D },
      ]}
    />
  );
}

// Factual flag popover — 4 frames.
function FFAmber(): JSX.Element {
  return (
    <Shell step={1} total={4} title="Amber marker">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Six weeks is the right interval{' '}
        <span className="bg-warning/15 text-warning rounded px-1">
          for every machine
        </span>
        .
      </p>
    </Shell>
  );
}

function FFPop(): JSX.Element {
  return (
    <Shell step={2} total={4} title="Popover opens">
      <div className="rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
        <div className="text-[10px] font-mono uppercase text-warning">
          Reviewer flagged
        </div>
        <div className="mt-1 text-dash-primary">
          Overstated — interval depends on water hardness.
        </div>
      </div>
    </Shell>
  );
}

function FFButtons(): JSX.Element {
  return (
    <Shell step={3} total={4} title="Pick a resolution">
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {['Verified', 'Softened', 'Removed', 'Other', 'Dispute'].map((b, i) => (
          <span
            key={b}
            className={
              i === 1
                ? 'rounded border border-accent/40 bg-accent/15 text-accent px-2 py-1 text-center'
                : 'rounded border border-dash-border bg-dash-surface text-dash-secondary px-2 py-1 text-center'
            }
          >
            {b}
          </span>
        ))}
      </div>
    </Shell>
  );
}

function FFResolved(): JSX.Element {
  return (
    <Shell step={4} total={4} title="Resolved">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Six to eight weeks is typical on city water; filtered water can
        stretch to twelve.
      </p>
      <div className="mt-2 text-[10px] font-mono uppercase tracking-wide text-success">
        flag acknowledged
      </div>
    </Shell>
  );
}

export function FactualFlagPopoverSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <FFAmber />, label: 'Click amber text', duration: D },
        { frame: <FFPop />, label: 'See reviewer note', duration: D },
        { frame: <FFButtons />, label: 'Pick a resolution', duration: D },
        { frame: <FFResolved />, label: 'Marker clears', duration: D },
      ]}
    />
  );
}

// Authority citation popover — 3 frames.
function ACToggle(): JSX.Element {
  return (
    <Shell step={1} total={3} title="Toggle visibility">
      <div className="flex gap-2 text-[11px] font-mono">
        {['Illustrations', 'Links', 'Flags', 'Citations', 'Annotations'].map((t) => (
          <span
            key={t}
            className={
              t === 'Citations'
                ? 'rounded border border-accent/40 bg-accent/15 text-accent px-2 py-0.5'
                : 'rounded border border-dash-border text-dash-muted px-2 py-0.5'
            }
          >
            {t}
          </span>
        ))}
      </div>
    </Shell>
  );
}

function ACDotted(): JSX.Element {
  return (
    <Shell step={2} total={3} title="Dotted underlines">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Most cardiologists agree that hydration{' '}
        <span
          className="underline decoration-dotted"
          style={{ textDecorationColor: '#a78bfa' }}
        >
          lowers stroke risk
        </span>{' '}
        marginally.
      </p>
    </Shell>
  );
}

function ACPop(): JSX.Element {
  return (
    <Shell step={3} total={3} title="Skip or Replace">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2 text-xs">
        <div className="text-[10px] font-mono uppercase text-dash-muted">
          Suggested source
        </div>
        <div className="text-dash-primary">AHA: Hydration and stroke</div>
        <div className="mt-2 flex gap-2 text-[11px]">
          <span className="rounded border border-dash-border text-dash-secondary px-2 py-0.5">
            Skip
          </span>
          <span className="rounded bg-accent/15 text-accent border border-accent/40 px-2 py-0.5">
            Replace
          </span>
        </div>
      </div>
    </Shell>
  );
}

export function AuthorityCitationSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <ACToggle />, label: 'Toggle citations on', duration: D },
        { frame: <ACDotted />, label: 'Dotted underlines appear', duration: D },
        { frame: <ACPop />, label: 'Skip or replace each', duration: D },
      ]}
    />
  );
}

// Annotation create-and-resolve — 5 frames.
function ANSelect(): JSX.Element {
  return (
    <Shell step={1} total={5} title="Select text">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Espresso machines need a descale every{' '}
        <span className="bg-accent/20 text-dash-primary rounded">
          six to eight weeks
        </span>{' '}
        on city water.
      </p>
    </Shell>
  );
}

function ANPill(): JSX.Element {
  return (
    <Shell step={2} total={5} title="Annotate pill">
      <p className="text-xs text-dash-secondary leading-relaxed">
        Espresso machines need a descale every{' '}
        <span className="bg-accent/20 text-dash-primary rounded">
          six to eight weeks
        </span>{' '}
        on city water.
      </p>
      <div className="mt-2 inline-flex rounded bg-violet-400/15 text-violet-300 border border-violet-400/40 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide">
        Annotate
      </div>
    </Shell>
  );
}

function ANCompose(): JSX.Element {
  return (
    <Shell step={3} total={5} title="Compose note">
      <div className="rounded border border-violet-400/40 bg-violet-400/10 px-3 py-2 text-xs">
        <div className="text-[10px] font-mono uppercase text-violet-300">
          New annotation
        </div>
        <div className="mt-1 rounded border border-dash-border bg-dash-bg px-2 py-1 text-dash-primary">
          Cite a study with the 6-8 week figure.
        </div>
      </div>
    </Shell>
  );
}

function ANSidebar(): JSX.Element {
  return (
    <Shell step={4} total={5} title="Sidebar row">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="rounded border border-purple-400/40 bg-purple-400/10 text-purple-300 px-1.5 py-0.5 text-[10px] font-mono">
            Inline
          </span>
          <span className="text-dash-secondary truncate">
            "six to eight weeks"
          </span>
        </div>
        <div className="mt-1 text-dash-primary">
          Cite a study with the 6-8 week figure.
        </div>
      </div>
    </Shell>
  );
}

function ANResolved(): JSX.Element {
  return (
    <Shell step={5} total={5} title="Resolved">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2 text-xs opacity-70">
        <div className="flex items-center gap-2">
          <span className="rounded border border-purple-400/40 bg-purple-400/10 text-purple-300 px-1.5 py-0.5 text-[10px] font-mono">
            Inline
          </span>
          <span className="ml-auto text-success text-[10px] font-mono">done</span>
        </div>
        <div className="mt-1 text-dash-muted line-through">
          Cite a study with the 6-8 week figure.
        </div>
      </div>
    </Shell>
  );
}

export function AnnotationSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <ANSelect />, label: 'Select inline text', duration: D },
        { frame: <ANPill />, label: 'Click Annotate', duration: D },
        { frame: <ANCompose />, label: 'Type a note', duration: D },
        { frame: <ANSidebar />, label: 'Sidebar row appears', duration: D },
        { frame: <ANResolved />, label: 'Resolve when fixed', duration: D },
      ]}
    />
  );
}

// Illustration generate/regenerate — 4 frames.
function IllPrompt(): JSX.Element {
  return (
    <Shell step={1} total={4} title="[Illustration] card">
      <div className="rounded border border-accent/40 bg-accent/5 px-3 py-2 text-xs">
        <div className="text-[10px] font-mono uppercase tracking-wide text-accent">
          [Illustration]
        </div>
        <div className="mt-1 text-dash-primary">
          Diagram showing flow restriction inside a scaled boiler.
        </div>
        <div className="mt-2 inline-flex rounded bg-accent/15 text-accent border border-accent/40 px-2 py-0.5">
          Generate
        </div>
      </div>
    </Shell>
  );
}

function IllLoading(): JSX.Element {
  return (
    <Shell step={2} total={4} title="Claude draws">
      <div className="rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
        <div className="text-warning font-mono uppercase text-[10px]">
          generating…
        </div>
        <div className="mt-2 h-1 rounded bg-dash-bg overflow-hidden">
          <div className="h-full w-2/3 bg-warning/60" />
        </div>
      </div>
    </Shell>
  );
}

function IllSvg(): JSX.Element {
  return (
    <Shell step={3} total={4} title="SVG arrives">
      <div className="rounded border border-dash-border bg-dash-bg p-3">
        <svg viewBox="0 0 120 50" className="w-full h-12">
          <rect
            x="4"
            y="14"
            width="112"
            height="22"
            rx="3"
            className="fill-dash-surface stroke-dash-border"
          />
          <circle cx="22" cy="25" r="3" className="fill-warning" />
          <circle cx="42" cy="25" r="4" className="fill-warning" />
          <circle cx="62" cy="25" r="5" className="fill-warning" />
          <circle cx="82" cy="25" r="6" className="fill-warning" />
          <circle cx="102" cy="25" r="3" className="fill-accent" />
        </svg>
      </div>
    </Shell>
  );
}

function IllRegen(): JSX.Element {
  return (
    <Shell step={4} total={4} title="Regenerate with feedback">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2 text-xs">
        <div className="text-[10px] font-mono uppercase text-dash-muted">
          Feedback
        </div>
        <div className="mt-1 rounded border border-dash-border bg-dash-bg px-2 py-1 text-dash-primary">
          Use blue arrows, not amber.
        </div>
        <div className="mt-2 inline-flex rounded bg-accent/15 text-accent border border-accent/40 px-2 py-0.5">
          Regenerate
        </div>
      </div>
    </Shell>
  );
}

export function IllustrationSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <IllPrompt />, label: 'Click Generate', duration: D },
        { frame: <IllLoading />, label: 'Claude draws SVG', duration: D },
        { frame: <IllSvg />, label: 'SVG drops into the card', duration: D },
        { frame: <IllRegen />, label: 'Regenerate with feedback', duration: D },
      ]}
    />
  );
}

// Visibility toggles — 2 frames.
function VTOn(): JSX.Element {
  return (
    <Shell step={1} total={2} title="All layers on">
      <div className="flex gap-1 text-[10px] font-mono">
        {['Illust.', 'Links', 'Flags', 'Cit.', 'Anno.'].map((t) => (
          <span
            key={t}
            className="rounded border border-accent/40 bg-accent/10 text-accent px-1.5 py-0.5"
          >
            {t}
          </span>
        ))}
      </div>
      <p className="mt-2 text-xs text-dash-secondary leading-relaxed">
        Limescale{' '}
        <span className="underline decoration-accent decoration-dotted">
          chokes flow
        </span>{' '}
        within{' '}
        <span className="bg-warning/15 text-warning rounded px-1">
          a few months
        </span>{' '}
        of city water use.
      </p>
    </Shell>
  );
}

function VTOff(): JSX.Element {
  return (
    <Shell step={2} total={2} title="Layers off">
      <div className="flex gap-1 text-[10px] font-mono">
        {['Illust.', 'Links', 'Flags', 'Cit.', 'Anno.'].map((t) => (
          <span
            key={t}
            className="rounded border border-dash-border text-dash-muted px-1.5 py-0.5"
          >
            {t}
          </span>
        ))}
      </div>
      <p className="mt-2 text-xs text-dash-secondary leading-relaxed">
        Limescale chokes flow within a few months of city water use.
      </p>
    </Shell>
  );
}

export function VisibilityToggleSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <VTOn />, label: 'Click the eye icons', duration: D },
        { frame: <VTOff />, label: 'Decorations hide', duration: D },
      ]}
    />
  );
}

// Focus mode — 2 frames.
function FMOn(): JSX.Element {
  return (
    <Shell step={1} total={2} title="Annotations visible">
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <p className="text-xs text-dash-secondary leading-relaxed">
          A focused mind beats a noisy one.
        </p>
        <div className="rounded border border-violet-400/40 bg-violet-400/10 px-2 py-1.5 text-[11px] text-dash-primary">
          <div className="font-semibold text-violet-300">Add data</div>
          <div className="text-dash-secondary">Cite the study.</div>
        </div>
      </div>
    </Shell>
  );
}

function FMHidden(): JSX.Element {
  return (
    <Shell step={2} total={2} title="Focus mode on">
      <p className="text-xs text-dash-secondary leading-relaxed">
        A focused mind beats a noisy one.
      </p>
      <div className="mt-2 text-[10px] font-mono uppercase tracking-wide text-dash-muted">
        sidebar hidden — Esc to exit
      </div>
    </Shell>
  );
}

export function FocusModeSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <FMOn />, label: 'Annotations beside prose', duration: D },
        { frame: <FMHidden />, label: 'Click Focus to hide them', duration: D },
      ]}
    />
  );
}
